import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircleQuestion, X, Send, Loader2 } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = 'hg_help';
const inputCls = 'w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-lg px-3 py-2 text-[13.5px] outline-none focus:border-brand';
const loadRef = () => { try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch { return null; } };
const fmtTime = (iso) => new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const Bubble = ({ m }) => (
  <div className={`flex ${m.sender === 'customer' ? 'justify-end' : 'justify-start'}`} data-testid={`help-msg-${m.sender}`}>
    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-[13.5px] leading-relaxed whitespace-pre-wrap break-words ${m.sender === 'customer' ? 'bg-brand text-white rounded-br-md' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100 rounded-bl-md'}`}>
      {m.text}
      <div className={`mt-1 text-[10.5px] ${m.sender === 'customer' ? 'text-white/70' : 'text-neutral-500'}`}>{m.sender === 'admin' ? 'Support · ' : ''}{fmtTime(m.created_at)}</div>
    </div>
  </div>
);

const StartForm = ({ isAuthed, email, onStart, busy }) => {
  const [f, setF] = useState({ name: '', email: '', message: '' });
  const upd = (k) => (e) => setF(prev => ({ ...prev, [k]: e.target.value }));
  return (
    <form className="p-4 space-y-2.5" onSubmit={(e) => { e.preventDefault(); onStart(f); }} data-testid="help-start-form">
      <p className="text-[13px] text-neutral-600 dark:text-neutral-300">Send us a message and we'll reply here{isAuthed ? '' : ' and by email'}.</p>
      {isAuthed
        ? <p className="text-[12px] text-neutral-500">Signed in as <span className="font-medium text-neutral-700 dark:text-neutral-200">{email}</span></p>
        : <>
          <input data-testid="help-name-input" className={inputCls} placeholder="Your name" maxLength={60} value={f.name} onChange={upd('name')} required />
          <input data-testid="help-email-input" type="email" className={inputCls} placeholder="Your email" value={f.email} onChange={upd('email')} required />
        </>}
      <textarea data-testid="help-start-message" className={inputCls + ' resize-none'} rows={4} placeholder="How can we help?" maxLength={2000} value={f.message} onChange={upd('message')} required />
      <button data-testid="help-start-submit" disabled={busy} className="btn-brand w-full rounded-lg py-2.5 text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={14} />} Send message
      </button>
    </form>
  );
};

const HelpWidget = () => {
  const { auth, isAuthed, headers } = useCustomerAuth();
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState(loadRef);
  const [conv, setConv] = useState(null);
  const [unread, setUnread] = useState(0);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const endRef = useRef(null);

  const saveRef = (r) => { localStorage.setItem(KEY, JSON.stringify(r)); setRef(r); };

  const fetchConv = useCallback(async () => {
    try {
      if (ref) {
        const { data } = await axios.get(`${API}/help/conversations/${ref.id}`, { params: { token: ref.token }, headers });
        setConv(data); setUnread(0);
      } else if (isAuthed) {
        const { data } = await axios.get(`${API}/help/mine`, { headers });
        if (data) { saveRef({ id: data.id, token: data.token }); setConv(data); }
      }
    } catch (e) {
      if (e?.response?.status === 404) { localStorage.removeItem(KEY); setRef(null); setConv(null); }
    }
  }, [ref, isAuthed, headers]);

  useEffect(() => {
    if (!open) return undefined;
    fetchConv();
    const t = setInterval(fetchConv, 8000);
    return () => clearInterval(t);
  }, [open, fetchConv]);

  useEffect(() => {
    if (open || !ref) return undefined;
    const poll = () => axios.get(`${API}/help/conversations/${ref.id}/unread`, { params: { token: ref.token } })
      .then(({ data }) => setUnread(data.unread)).catch(() => {});
    poll();
    const t = setInterval(poll, 45000);
    return () => clearInterval(t);
  }, [open, ref]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [conv?.messages?.length, open]);

  const start = async (f) => {
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post(`${API}/help/conversations`, { name: f.name, email: f.email, message: f.message }, { headers });
      saveRef({ id: data.id, token: data.token }); setConv(data);
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not send. Please try again.'); }
    finally { setBusy(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || !conv) return;
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post(`${API}/help/conversations/${conv.id}/messages`, { token: ref?.token, text: t }, { headers });
      setConv(prev => ({ ...prev, status: 'open', messages: [...prev.messages, data] })); setText('');
    } catch (e2) { setErr(e2?.response?.data?.detail || 'Could not send. Please try again.'); }
    finally { setBusy(false); }
  };

  const newChat = () => { localStorage.removeItem(KEY); setRef(null); setConv(null); };

  return (
    <>
      {open && (
        <div data-testid="help-panel" className="fixed z-[60] bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-[370px] max-h-[min(560px,calc(100vh-8rem))] flex flex-col rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] shadow-2xl overflow-hidden page-fade-in">
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[hsl(var(--brand-strong))] to-[hsl(var(--brand))] text-white">
            <div>
              <div className="font-semibold text-[14.5px]">Need help?</div>
              <div className="text-[11.5px] text-white/80">We usually reply within a few hours</div>
            </div>
            <button data-testid="help-close-btn" onClick={() => setOpen(false)} aria-label="Close help" className="p-1.5 rounded-full hover:bg-white/15"><X size={16} /></button>
          </div>
          {conv ? (
            <>
              <div data-testid="help-messages" className="flex-1 overflow-y-auto p-4 space-y-2.5 bg-neutral-50/60 dark:bg-transparent">
                {conv.messages.map(m => <Bubble key={m.id} m={m} />)}
                {conv.status === 'closed' && <p className="text-center text-[11.5px] text-neutral-500 pt-1">This conversation was marked resolved. Send a message to reopen it.</p>}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="border-t border-neutral-200 dark:border-neutral-800 p-2.5 flex items-end gap-2">
                <textarea data-testid="help-message-input" rows={1} value={text} maxLength={2000} placeholder="Type a message…"
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) send(e); }}
                  className={inputCls + ' resize-none max-h-28'} />
                <button data-testid="help-send-btn" disabled={busy || !text.trim()} aria-label="Send" className="btn-brand shrink-0 w-10 h-10 rounded-full grid place-items-center disabled:opacity-50">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                </button>
              </form>
              <button data-testid="help-new-chat" onClick={newChat} className="text-[11.5px] text-neutral-500 hover:text-brand pb-2">Start a new conversation</button>
            </>
          ) : (
            <StartForm isAuthed={isAuthed} email={auth?.email} onStart={start} busy={busy} />
          )}
          {err && <p data-testid="help-error" className="px-4 pb-3 text-[12px] text-red-600">{err}</p>}
        </div>
      )}
      <button data-testid="help-toggle-btn" onClick={() => setOpen(o => !o)} aria-label="Need help?"
        className="fixed z-[60] bottom-5 right-4 sm:right-6 h-14 pl-4 pr-5 rounded-full btn-brand shadow-xl shadow-[hsl(var(--brand)/0.35)] inline-flex items-center gap-2 font-semibold text-[14px] hover:-translate-y-0.5 transition-transform">
        {open ? <X size={20} /> : <MessageCircleQuestion size={20} />}
        <span className="hidden sm:inline">{open ? 'Close' : 'Need help?'}</span>
        {!open && unread > 0 && <span data-testid="help-unread-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[11px] grid place-items-center">{unread}</span>}
      </button>
    </>
  );
};

export default HelpWidget;
